import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Filter,
  UserPlus,
  Mail,
  Shield,
  Ban,
  Trash2,
  Edit2,
  CheckCircle2,
  XCircle,
  X,
  Eye,
  EyeOff,
  Camera,
  MapPin,
  Globe,
  FileText,
  Lock,
  User,
  AtSign,
  ShieldCheck,
  Upload,
  CheckCheck,
  AlertCircle,
} from 'lucide-react';
import { cn } from '../../utils';
import { profileService } from '../../lib/profileService';
import MediaRender from '../../components/MediaRender';
import { useAuth } from '../../contexts/AuthContext';

/* ─────────────────────────── helpers ─────────────────────────── */

function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  const checks = {
    length:    pw.length >= 8,
    lowercase: /[a-z]/.test(pw),
    uppercase: /[A-Z]/.test(pw),
    number:    /\d/.test(pw),
    special:   /[!@#$%^&*(),.?":{}|<>]/.test(pw),
  };
  const score = Object.values(checks).filter(Boolean).length;
  if (score < 2) return { score, label: 'Weak',   color: '#ef4444', checks };
  if (score < 4) return { score, label: 'Fair',   color: '#f59e0b', checks };
  if (score < 5) return { score, label: 'Good',   color: '#10b981', checks };
  return           { score, label: 'Strong', color: '#6366f1', checks };
}

const EMPTY_ADD_FORM = {
  profilePhoto: null,
  full_name: '',
  username: '',
  email: '',
  password: '',
  bio: '',
  location: '',
  website: '',
  role: 'User',
  is_admin: false,
};

const EMPTY_EDIT_FORM = {
  full_name: '',
  username: '',
  location: '',
  bio: '',
  website: '',
  role: 'User',
  is_admin: false,
};

/* ────────────────── Password Strength Bar ────────────────── */
function StrengthBar({ password }) {
  const { score, label, color, checks } = getPasswordStrength(password);
  if (!password) return null;
  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-white/40">Password strength</span>
        <span className="text-xs font-bold" style={{ color }}>{label}</span>
      </div>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map(n => (
          <div
            key={n}
            className="h-1.5 flex-1 rounded-full transition-all duration-300"
            style={{ background: n <= score ? color : 'rgba(255,255,255,0.1)' }}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-1">
        {[
          ['8+ characters', checks?.length],
          ['Uppercase letter', checks?.uppercase],
          ['Lowercase letter', checks?.lowercase],
          ['Number', checks?.number],
          ['Special character', checks?.special],
        ].map(([label, ok]) => (
          <div key={label} className="flex items-center gap-1">
            {ok
              ? <CheckCheck size={11} className="text-emerald-400 flex-shrink-0" />
              : <AlertCircle size={11} className="text-white/20 flex-shrink-0" />}
            <span className={`text-[10px] ${ok ? 'text-emerald-400' : 'text-white/30'}`}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────────── Avatar Picker (inline) ───────────────── */
function AvatarPicker({ value, onChange }) {
  const ref = useRef(null);
  const [preview, setPreview] = useState(null);

  const pick = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      onChange(file);
    };
    reader.readAsDataURL(file);
  };

  const remove = () => {
    setPreview(null);
    onChange(null);
    if (ref.current) ref.current.value = '';
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        {preview ? (
          <img
            src={preview}
            alt="avatar"
            className="w-20 h-20 rounded-full object-cover border-2 border-violet-500/40"
          />
        ) : (
          <div className="w-20 h-20 rounded-full bg-white/5 border-2 border-dashed border-white/20 flex items-center justify-center">
            <Camera size={22} className="text-white/30" />
          </div>
        )}
        <button
          type="button"
          onClick={() => ref.current?.click()}
          className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-violet-600 flex items-center justify-center shadow-lg hover:bg-violet-500 transition-colors"
        >
          <Upload size={13} className="text-white" />
        </button>
        {preview && (
          <button
            type="button"
            onClick={remove}
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-600 flex items-center justify-center"
          >
            <X size={10} className="text-white" />
          </button>
        )}
      </div>
      <span className="text-[10px] text-white/30">Click camera to upload</span>
      <input ref={ref} type="file" accept="image/*" className="sr-only"
        onChange={e => pick(e.target.files?.[0])} />
    </div>
  );
}

/* ─────────── Reusable field label ─────────── */
const FieldLabel = ({ children }) => (
  <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1.5">
    {children}
  </label>
);

/* ─────────── Styled input ─────────── */
const AdminInput = ({ icon: Icon, ...props }) => (
  <div className="relative">
    {Icon && (
      <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none" />
    )}
    <input
      {...props}
      className={`w-full bg-white/5 border border-white/10 rounded-xl py-2.5 text-sm text-white placeholder-white/20 outline-none
        focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/30 transition-all
        ${Icon ? 'pl-9 pr-3' : 'px-3'}`}
    />
  </div>
);

/* ─────────── Styled textarea ─────────── */
const AdminTextarea = ({ icon: Icon, ...props }) => (
  <div className="relative">
    {Icon && (
      <Icon size={15} className="absolute left-3 top-3 text-white/25 pointer-events-none" />
    )}
    <textarea
      {...props}
      className={`w-full bg-white/5 border border-white/10 rounded-xl py-2.5 text-sm text-white placeholder-white/20 outline-none
        focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/30 transition-all resize-none
        ${Icon ? 'pl-9 pr-3' : 'px-3'}`}
    />
  </div>
);

/* ─────────── Styled select ─────────── */
const AdminSelect = ({ icon: Icon, children, ...props }) => (
  <div className="relative">
    {Icon && (
      <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none" />
    )}
    <select
      {...props}
      className={`w-full bg-white/5 border border-white/10 rounded-xl py-2.5 text-sm text-white outline-none
        focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/30 transition-all appearance-none
        ${Icon ? 'pl-9 pr-3' : 'px-3'}`}
    >
      {children}
    </select>
  </div>
);

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════ */
const UserManagement = () => {
  const { signUp } = useAuth();

  /* ── table state ── */
  const [users, setUsers]               = useState([]);
  const [searchTerm, setSearchTerm]     = useState('');
  const [selectedRole, setSelectedRole] = useState('All Roles');
  const [isLoading, setIsLoading]       = useState(true);

  /* ── edit modal ── */
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm]       = useState(EMPTY_EDIT_FORM);
  const [isSaving, setIsSaving]       = useState(false);

  /* ── add modal ── */
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [addForm, setAddForm]           = useState(EMPTY_ADD_FORM);
  const [isAdding, setIsAdding]         = useState(false);
  const [addError, setAddError]         = useState('');
  const [addSuccess, setAddSuccess]     = useState('');
  const [showPassword, setShowPassword] = useState(false);

  /* ──────────────── fetch ──────────────── */
  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const profiles = await profileService.getAllProfiles();
      const mapped = profiles.map(p => ({
        id:       p.id,
        name:     p.full_name   || 'Anonymous User',
        username: p.username    || 'username',
        email:    p.email       || `${p.username || 'user'}@aether.com`,
        role:     p.role        || 'User',
        status:   p.status      || 'Active',
        joined:   new Date(p.created_at).toLocaleDateString(),
        reports:  p.reports_count || 0,
        avatar:   p.avatar_url  || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop',
        bio:      p.bio         || '',
        location: p.location    || '',
        website:  p.website     || '',
        is_admin: !!p.is_admin,
      }));
      setUsers(mapped);
    } catch (e) {
      console.error('Failed to load admin users:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  /* ──────────────── add user ──────────────── */
  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddError('');
    setAddSuccess('');

    /* validation */
    if (!addForm.full_name.trim())  return setAddError('Full name is required.');
    if (!addForm.username.trim())   return setAddError('Username is required.');
    if (!addForm.email.trim())      return setAddError('Email is required.');
    if (addForm.password.length < 6) return setAddError('Password must be at least 6 characters.');

    setIsAdding(true);
    try {
      /* 1. Create auth account via the existing signUp flow */
      const { data, error } = await signUp({
        email: addForm.email,
        password: addForm.password,
        options: {
          data: {
            full_name: addForm.full_name,
            username:  addForm.username,
          },
        },
      });
      if (error) throw error;

      const userId = data?.user?.id;

      /* 2. Upload avatar if provided */
      let avatarUrl = null;
      if (addForm.profilePhoto && userId) {
        try {
          avatarUrl = await profileService.uploadAvatar(userId, addForm.profilePhoto);
        } catch {/* non-fatal */}
      }

      /* 3. Save extended profile fields */
      const profilePayload = {
        full_name:  addForm.full_name,
        username:   addForm.username,
        bio:        addForm.bio,
        location:   addForm.location,
        website:    addForm.website,
        role:       addForm.role,
        is_admin:   addForm.is_admin || addForm.role === 'Admin',
        status:     'Active',
        avatar_url: avatarUrl,
        created_at: new Date().toISOString(),
      };

      if (userId) {
        await profileService.updateProfile(userId, profilePayload);
      } else {
        /* Fallback: write directly into mock localStorage */
        const id = 'mock-' + Date.now();
        const profiles = JSON.parse(localStorage.getItem('profiles') || '{}');
        profiles[id] = { id, email: addForm.email, ...profilePayload };
        localStorage.setItem('profiles', JSON.stringify(profiles));
      }

      setAddSuccess(`✓ User "${addForm.full_name}" registered successfully!`);
      setAddForm(EMPTY_ADD_FORM);
      setShowPassword(false);
      await fetchUsers();

      /* Auto-close after 1.5 s */
      setTimeout(() => {
        setIsAddingUser(false);
        setAddSuccess('');
      }, 1500);
    } catch (err) {
      console.error(err);
      setAddError(err?.message || 'Registration failed. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  /* ──────────────── edit user ──────────────── */
  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditForm({
      full_name: user.name,
      username:  user.username,
      location:  user.location,
      bio:       user.bio,
      website:   user.website || '',
      role:      user.role,
      is_admin:  user.is_admin,
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    setIsSaving(true);
    try {
      await profileService.updateProfile(editingUser.id, {
        full_name: editForm.full_name,
        username:  editForm.username,
        location:  editForm.location,
        bio:       editForm.bio,
        website:   editForm.website,
        role:      editForm.role,
        is_admin:  editForm.is_admin || editForm.role === 'Admin',
      });
      setEditingUser(null);
      await fetchUsers();
    } catch (err) {
      console.error(err);
      alert('Failed to update user profile');
    } finally {
      setIsSaving(false);
    }
  };

  /* ──────────────── toggle status ──────────────── */
  const handleToggleStatus = async (user) => {
    const next = user.status === 'Active' ? 'Suspended' : 'Active';
    try {
      await profileService.updateProfile(user.id, { status: next });
      await fetchUsers();
    } catch (err) {
      console.error(err);
      alert('Failed to toggle status');
    }
  };

  /* ──────────────── delete user ──────────────── */
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Delete "${user.name}"? This cannot be undone.`)) return;
    try {
      const local = JSON.parse(localStorage.getItem('profiles') || '{}');
      delete local[user.id];
      localStorage.setItem('profiles', JSON.stringify(local));
      await fetchUsers();
    } catch (err) {
      console.error(err);
      alert('Failed to delete user');
    }
  };

  /* ──────────────── filter ──────────────── */
  const filteredUsers = users.filter(u => {
    const s  = searchTerm.toLowerCase();
    const ok = u.name.toLowerCase().includes(s) ||
               u.email.toLowerCase().includes(s) ||
               u.username.toLowerCase().includes(s);
    const r  = selectedRole === 'All Roles' || u.role === selectedRole;
    return ok && r;
  });

  /* ═══════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════ */
  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold gradient-text">User Management</h1>
          <p className="text-white/40 mt-1 text-sm">Manage and monitor Aether community members.</p>
        </div>
        <button
          onClick={() => { setIsAddingUser(true); setAddError(''); setAddSuccess(''); setAddForm(EMPTY_ADD_FORM); }}
          className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white rounded-xl font-semibold transition-all shadow-lg shadow-violet-900/30 w-fit"
        >
          <UserPlus size={18} />
          Register New User
        </button>
      </div>

      {/* Filters */}
      <div className="glass-panel admin-card p-4 rounded-2xl flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={18} />
          <input
            type="text"
            placeholder="Search users by name, username or email..."
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl text-sm focus:ring-1 focus:ring-violet-500/50 focus:border-violet-500/50 outline-none text-white placeholder-white/30"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2 w-full md:w-auto">
          <select
            className="flex-1 md:w-40 bg-white/5 border border-white/10 rounded-xl text-sm py-2 px-4 outline-none text-white/60 focus:ring-1 focus:ring-violet-500/50"
            value={selectedRole}
            onChange={e => setSelectedRole(e.target.value)}
          >
            <option>All Roles</option>
            <option>Admin</option>
            <option>Moderator</option>
            <option>User</option>
          </select>
          <button className="p-2 glass-panel border border-white/10 text-white/40 rounded-xl hover:text-white hover:border-white/30 transition-all">
            <Filter size={20} />
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="admin-card rounded-3xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-white/5">
                {['User', 'Role', 'Status', 'Joined', 'Reports', 'Actions'].map((h, i) => (
                  <th key={h} className={`px-6 py-4 text-xs font-bold text-white/30 uppercase tracking-wider ${i >= 4 ? 'text-center' : ''} ${i === 5 ? 'text-right' : ''}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {isLoading ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center text-white/30">Loading users…</td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-10 text-center text-white/30">No users found.</td></tr>
              ) : filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-white/5 transition-colors group">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <MediaRender type="image" src={user.avatar} alt={user.name}
                        className="w-10 h-10 rounded-full object-cover ring-2 ring-violet-500/20" />
                      <div>
                        <p className="text-sm font-semibold text-white group-hover:text-violet-300 transition-colors">{user.name}</p>
                        <p className="text-xs text-white/40">@{user.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border',
                      user.role === 'Admin'     ? 'bg-violet-500/15 text-violet-300 border-violet-500/30' :
                      user.role === 'Moderator' ? 'bg-blue-500/15 text-blue-300 border-blue-500/30' :
                      'bg-white/5 text-white/50 border-white/10'
                    )}>
                      <Shield size={11} /> {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border',
                      user.status === 'Active'    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' :
                      user.status === 'Suspended' ? 'bg-rose-500/15 text-rose-300 border-rose-500/30' :
                      'bg-amber-500/15 text-amber-300 border-amber-500/30'
                    )}>
                      {user.status === 'Active'    ? <CheckCircle2 size={11} /> :
                       user.status === 'Suspended' ? <XCircle size={11} /> :
                       <div className="w-2 h-2 rounded-full bg-current" />}
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-white/40">{user.joined}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={cn('text-sm font-bold', user.reports > 0 ? 'text-rose-400' : 'text-white/20')}>
                      {user.reports}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEditClick(user)}
                        className="p-2 text-white/30 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-all" title="Edit">
                        <Edit2 size={15} />
                      </button>
                      <button onClick={() => handleToggleStatus(user)}
                        className="p-2 text-white/30 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-all" title="Toggle Ban">
                        <Ban size={15} />
                      </button>
                      <button onClick={() => handleDeleteUser(user)}
                        className="p-2 text-white/30 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-all" title="Delete">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ─────────── EDIT USER MODAL ─────────── */}
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setEditingUser(null)} />
          <div className="relative glass-panel border border-white/10 rounded-[28px] max-w-md w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-white">Edit User Profile</h3>
                <p className="text-xs text-white/40 mt-0.5">Admin overwrite — changes save immediately</p>
              </div>
              <button onClick={() => setEditingUser(null)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors">
                <X size={18} className="text-white/50" />
              </button>
            </div>

            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <FieldLabel>Full Name</FieldLabel>
                <AdminInput icon={User} type="text" placeholder="Full Name" required
                  value={editForm.full_name}
                  onChange={e => setEditForm({ ...editForm, full_name: e.target.value })} />
              </div>

              <div>
                <FieldLabel>Username</FieldLabel>
                <AdminInput icon={AtSign} type="text" placeholder="username" required
                  value={editForm.username}
                  onChange={e => setEditForm({ ...editForm, username: e.target.value })} />
              </div>

              <div>
                <FieldLabel>Location</FieldLabel>
                <AdminInput icon={MapPin} type="text" placeholder="City, Country"
                  value={editForm.location}
                  onChange={e => setEditForm({ ...editForm, location: e.target.value })} />
              </div>

              <div>
                <FieldLabel>Website</FieldLabel>
                <AdminInput icon={Globe} type="url" placeholder="https://example.com"
                  value={editForm.website}
                  onChange={e => setEditForm({ ...editForm, website: e.target.value })} />
              </div>

              <div>
                <FieldLabel>Bio</FieldLabel>
                <AdminTextarea icon={FileText} rows={3} placeholder="Short bio…"
                  value={editForm.bio}
                  onChange={e => setEditForm({ ...editForm, bio: e.target.value })} />
              </div>

              <div>
                <FieldLabel>Role</FieldLabel>
                <AdminSelect icon={Shield}
                  value={editForm.role}
                  onChange={e => setEditForm({ ...editForm, role: e.target.value, is_admin: e.target.value === 'Admin' })}>
                  <option value="User">User</option>
                  <option value="Moderator">Moderator</option>
                  <option value="Admin">Admin</option>
                </AdminSelect>
              </div>

              <label className="flex items-center gap-3 cursor-pointer py-2 px-3 bg-white/5 rounded-xl border border-white/10 hover:border-violet-500/30 transition-colors">
                <input
                  type="checkbox"
                  checked={editForm.is_admin}
                  onChange={e => setEditForm({ ...editForm, is_admin: e.target.checked })}
                  className="w-4 h-4 accent-violet-500 rounded"
                />
                <div>
                  <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                    <ShieldCheck size={14} className="text-violet-400" /> Grant Admin Privileges
                  </p>
                  <p className="text-[10px] text-white/30">Allows full access to the admin dashboard</p>
                </div>
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditingUser(null)}
                  className="px-5 py-2.5 rounded-xl border border-white/10 text-white/60 font-semibold text-sm hover:bg-white/5 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={isSaving}
                  className="px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold text-sm disabled:opacity-50 transition-colors">
                  {isSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─────────── REGISTER NEW USER MODAL ─────────── */}
      {isAddingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => { setIsAddingUser(false); setAddError(''); }} />
          <div className="relative glass-panel sm:border border-white/10 sm:rounded-[28px] max-w-lg w-full shadow-2xl h-full sm:h-auto sm:max-h-[92vh] flex flex-col">

            {/* Modal Header */}
            <div className="shrink-0 flex items-center justify-between px-6 py-5 border-b border-white/10 bg-black/20"
              style={{ backdropFilter: 'blur(20px)' }}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                  <UserPlus size={17} className="text-violet-300" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Register New User</h3>
                  <p className="text-[10px] text-white/30">Admin-created account · All fields</p>
                </div>
              </div>
              <button onClick={() => { setIsAddingUser(false); setAddError(''); }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors">
                <X size={18} className="text-white/50" />
              </button>
            </div>

            <form onSubmit={handleAddUser} className="p-6 space-y-5 overflow-y-auto flex-1">

              {/* Error / Success banners */}
              {addError && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm">
                  <AlertCircle size={15} className="flex-shrink-0" />
                  {addError}
                </div>
              )}
              {addSuccess && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm">
                  <CheckCircle2 size={15} className="flex-shrink-0" />
                  {addSuccess}
                </div>
              )}

              {/* Profile Photo */}
              <div className="py-2">
                <FieldLabel>Profile Photo (optional)</FieldLabel>
                <AvatarPicker
                  value={addForm.profilePhoto}
                  onChange={file => setAddForm({ ...addForm, profilePhoto: file })}
                />
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] text-white/20 uppercase tracking-widest">Identity</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Full Name + Username */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Full Name *</FieldLabel>
                  <AdminInput icon={User} type="text" placeholder="Alex Johnson" required
                    value={addForm.full_name}
                    onChange={e => setAddForm({ ...addForm, full_name: e.target.value })} />
                </div>
                <div>
                  <FieldLabel>Username *</FieldLabel>
                  <AdminInput icon={AtSign} type="text" placeholder="alexj" required
                    value={addForm.username}
                    onChange={e => setAddForm({ ...addForm, username: e.target.value })} />
                </div>
              </div>

              {/* Email */}
              <div>
                <FieldLabel>Email Address *</FieldLabel>
                <AdminInput icon={Mail} type="email" placeholder="user@example.com" required
                  value={addForm.email}
                  onChange={e => setAddForm({ ...addForm, email: e.target.value })} />
              </div>

              {/* Password */}
              <div>
                <FieldLabel>Password *</FieldLabel>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 6 characters"
                    required
                    value={addForm.password}
                    onChange={e => setAddForm({ ...addForm, password: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-2.5 pl-9 pr-10 text-sm text-white placeholder-white/20 outline-none focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/30 transition-all"
                  />
                  <button type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                <StrengthBar password={addForm.password} />
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] text-white/20 uppercase tracking-widest">Profile Details</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Bio */}
              <div>
                <FieldLabel>Bio</FieldLabel>
                <AdminTextarea icon={FileText} rows={2} placeholder="Short bio or description…"
                  value={addForm.bio}
                  onChange={e => setAddForm({ ...addForm, bio: e.target.value })} />
              </div>

              {/* Location + Website */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Location</FieldLabel>
                  <AdminInput icon={MapPin} type="text" placeholder="City, Country"
                    value={addForm.location}
                    onChange={e => setAddForm({ ...addForm, location: e.target.value })} />
                </div>
                <div>
                  <FieldLabel>Website</FieldLabel>
                  <AdminInput icon={Globe} type="url" placeholder="https://…"
                    value={addForm.website}
                    onChange={e => setAddForm({ ...addForm, website: e.target.value })} />
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] text-white/20 uppercase tracking-widest">Permissions</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Role */}
              <div>
                <FieldLabel>Role *</FieldLabel>
                <AdminSelect icon={Shield}
                  value={addForm.role}
                  onChange={e => setAddForm({ ...addForm, role: e.target.value, is_admin: e.target.value === 'Admin' })}>
                  <option value="User">User — Standard member</option>
                  <option value="Moderator">Moderator — Can moderate content</option>
                  <option value="Admin">Admin — Full platform access</option>
                </AdminSelect>
              </div>

              {/* Admin flag */}
              <label className="flex items-start gap-3 cursor-pointer py-3 px-4 bg-white/5 rounded-xl border border-white/10 hover:border-violet-500/40 transition-colors">
                <input
                  type="checkbox"
                  checked={addForm.is_admin}
                  onChange={e => setAddForm({ ...addForm, is_admin: e.target.checked })}
                  className="mt-0.5 w-4 h-4 accent-violet-500 rounded flex-shrink-0"
                />
                <div>
                  <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                    <ShieldCheck size={14} className="text-violet-400" />
                    Grant Admin Dashboard Access
                  </p>
                  <p className="text-[10px] text-white/30 mt-0.5">
                    Enables full admin panel access regardless of role. Use carefully.
                  </p>
                </div>
              </label>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button"
                  onClick={() => { setIsAddingUser(false); setAddError(''); setAddForm(EMPTY_ADD_FORM); }}
                  className="px-5 py-2.5 rounded-xl border border-white/10 text-white/60 font-semibold text-sm hover:bg-white/5 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={isAdding}
                  className="px-6 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-bold text-sm disabled:opacity-50 transition-all shadow-lg shadow-violet-900/30 flex items-center gap-2">
                  {isAdding
                    ? <><span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Registering…</>
                    : <><UserPlus size={15} /> Register User</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
